import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AuthService } from '@presencelearning/auth';
import { filter, switchMap, take } from 'rxjs';
import { Appointment, clientName, selectDoorAppointment } from './appointments';
import { AppointmentsService } from './appointments.service';
import { ClassroomFrameComponent } from './classroom-frame.component';
import { ResourceOverlayComponent, SAMPLE_GIF_URL } from './resource-overlay.component';
import { RoomDoorService, RoomVendor } from './room-door.service';
import { RoomLink } from './room-link';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-provider-door',
  imports: [TranslocoPipe, ClassroomFrameComponent, ResourceOverlayComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './provider-door.component.html',
  styles: `
    :host {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      min-height: 0;
      min-width: 0;
    }
  `,
})
export class ProviderDoorComponent implements OnInit {
  private readonly appointmentsApi = inject(AppointmentsService);
  private readonly door = inject(RoomDoorService);
  private readonly auth = inject(AuthService);
  private readonly transloco = inject(TranslocoService);
  private readonly roomLink = inject(RoomLink);

  private roomDark(): boolean {
    return document.documentElement.classList.contains('pl-dark');
  }
  private readonly destroyRef = inject(DestroyRef);

  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly appointment = signal<Appointment | null>(null);
  protected readonly happeningNow = signal(false);
  protected readonly classroomUrl = signal<string | null>(null);
  protected readonly doorUnreachable = signal(false);
  protected readonly starting = signal(false);
  protected readonly overlayOpen = signal(false);
  protected readonly sampleGif = SAMPLE_GIF_URL;
  private overlayTrigger: HTMLElement | null = null;

  protected readonly clientName = clientName;
  protected readonly hilinkMeetings = environment.hilinkMeetings;

  // accessToken starts null. The guard can open this page before that signal
  // is filled, and the interceptor then sends this call with no Authorization
  // header. Wait for a token so the allowlisted request actually carries one.
  private readonly scheduleWhenAuthenticated$ = toObservable(this.auth.accessToken).pipe(
    filter((token): token is string => !!token),
    take(1),
    switchMap(() => this.appointmentsApi.list())
  );

  ngOnInit(): void {
    this.scheduleWhenAuthenticated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: appointments => {
        const selected = selectDoorAppointment(appointments);
        this.appointment.set(selected.appointment);
        this.happeningNow.set(selected.happeningNow);
        this.loading.set(false);
        if (selected.appointment && this.door.configured) {
          this.restoreClassroom(selected.appointment.uuid);
        }
      },
      error: () => {
        this.loading.set(false);
        this.error.set('door.scheduleFailed');
      },
    });
  }

  protected openOverlay(event: Event): void {
    this.overlayTrigger = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    this.overlayOpen.set(true);
  }

  protected closeOverlay(): void {
    this.overlayOpen.set(false);
    this.overlayTrigger?.focus();
    this.overlayTrigger = null;
  }

  protected openClassroom(): void {
    const id = this.appointment()?.uuid;
    if (!id) {
      return;
    }
    this.launchLeader(id);
  }

  protected startMeeting(vendor: RoomVendor): void {
    this.launchLeader(undefined, vendor);
  }

  private launchLeader(id: string | undefined, vendor?: RoomVendor): void {
    const provider = this.auth.currentUser();
    if (!provider || !this.door.configured) {
      this.doorUnreachable.set(true);
      return;
    }

    this.starting.set(true);
    this.error.set(null);
    this.door
      .openSpace({
        ...(id ? { id } : {}),
        ...(vendor ? { vendor } : {}),
        providerId: provider.id,
        providerName: provider.name,
        locale: this.transloco.getActiveLang(),
        darkmode: this.roomDark(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: space => {
          this.roomLink.set(space.id);
          this.classroomUrl.set(space.providerUrl);
          this.starting.set(false);
        },
        error: (error: HttpErrorResponse) => {
          this.starting.set(false);
          if (error.status === 0) {
            this.doorUnreachable.set(true);
            return;
          }
          const message = error.error?.error;
          this.error.set(typeof message === 'string' ? message : 'door.launchFailed');
        },
      });
  }

  private restoreClassroom(roomId: string): void {
    this.door
      .space(roomId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(space => {
        if (space?.providerUrl) {
          this.classroomUrl.set(space.providerUrl);
          this.roomLink.set(roomId);
        }
      });
  }
}
